import React, { useEffect, useState } from 'react';

import { Box, Card, Text, Flex, Image, Button } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import {
  UploadButton,
  UploadCard,
  UploadCardProps,
  UploadButtonProps,
} from '@opendoor/cinderblocks/sell/UploadCard';
import { FileUpload, getFileType } from 'shared/helpers/s3Helpers';
import { useObservability } from 'shared/helpers/observability';
import { Modal } from '@opendoor/bricks/complex';
import ReactPlayer from 'react-player';

export interface FileUploadCardProps
  extends Pick<UploadButtonProps, 'accept' | 'multiple' | 'size' | 'variant' | 'width'> {
  /**
   * text to show on the upload button.
   */
  buttonText: string;
  /**
   * title for displaying the uploaded file in modal.
   */
  modalDisplayTitle: string;
  /**
   * variant for renderring Card container.
   */
  cardVariant?: UploadCardProps['variant'];
  /**
   * specify if it should render the upload button with Card container.
   */
  renderCtaWithoutCardContainer?: boolean;
  /**
   * Uploaded files to be shown at first render in the uploaded state.
   */
  uploadedFiles?: FileUpload[];
  /**
   * if disable the upload feature.
   */
  uploadDisabled?: boolean;
  /**
   * if the component is for renderring the add another link.
   */
  addAnother?: boolean;
  /**
   * renders an optional field as a react node alongside the upload
   */
  optionalField?: React.ReactNode;
  /**
   * function to call to get s3 key for a file upload.
   */
  getS3Key: (file: File) => string;
  /**
   * function to call to upload file.
   * TODO: deprecate this prop and use shared upload to s3 function.
   */
  uploadFile: (fileUpload: FileUpload, onUpdate: (fileUpload: FileUpload) => void) => void;
  /**
   * Callback triggered when the first file upload starts.
   */
  onUploadStart?: () => void;
  /**
   * Callback triggered on each and every file upload complete.
   */
  onSingleUploadComplete?: (upload: FileUpload) => void;
  /**
   * Callback triggered when all uploads completes.
   */
  onAllUploadComplete?: (uploads: Array<FileUpload>) => void;
  /**
   * Callback triggered when upload fails.
   */
  onUploadError?: () => void;
  /**
   * Callback triggered when user click cancel icon, cancel icon will be hidden if onDeleteFile is not specified.
   */
  deleteFile?: (file: FileUpload) => void;
}

/**
 * FileUploadCard is used to upload a file to S3 and display different UI at each of the state of the uploads.
 * 1. when upload hasn't started, render a upload button.
 * 2. when file is uploading, render a upload card with progress bar.
 * 3. when file finished uploading, render a upload card with thumbnail, clickable to show the uploaded file, and a close icon to delete.
 * 4. if the upload supports multiple, display multiple upload card.
 */
export const FileUploadCard: React.FC<FileUploadCardProps> = ({
  cardVariant,
  uploadedFiles,
  variant,
  size,
  width,
  renderCtaWithoutCardContainer,
  uploadFile,
  getS3Key,
  onUploadStart,
  onSingleUploadComplete,
  onAllUploadComplete,
  onUploadError,
  deleteFile,
  multiple,
  accept,
  uploadDisabled,
  addAnother,
  buttonText,
  modalDisplayTitle,
  optionalField,
}) => {
  const { isMobile } = useDevice();
  const { trackEvent, trackError } = useObservability();

  const [files, setFiles] = useState<FileUpload[]>(uploadedFiles ?? []);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDisplayModal, setShowDisplayModal] = useState(false);

  useEffect(() => {
    setFiles(uploadedFiles ?? []);
  }, [uploadedFiles]);

  /**
   * onFileUploadUpdate is a callback function used to track s3 upload status.
   * The function can be called during the upload to get latest upload progress, or after upload finishes to get upload result.
   * 1. replace the old file in files state with the latest file.
   * 2. if file upload failed, track error and call onUploadError callback.
   * 3. if file upload completed, track event and call onUploadComplete callback.
   */
  const onFileUploadUpdate = (updatedFile: FileUpload) => {
    setFiles((oldFiles) => {
      if (oldFiles.length === 0) {
        return oldFiles;
      }
      // replace the file object before upload with uploaded version.
      const updatedFiles = oldFiles.map((oldFile) =>
        oldFile.s3Key === updatedFile.s3Key ? updatedFile : oldFile,
      );
      let hasError = false;
      let allCompleted = true;
      const parallelUploads = updatedFiles.length;

      for (const fileUpload of updatedFiles) {
        const metrics = {
          value: fileUpload.file.size / fileUpload.uploadLatency,
          parallelUploads: parallelUploads,
          size: fileUpload.file.size,
          isMultipart: updatedFile.multipartUpload,
          latency: updatedFile.uploadLatency,
        };

        if (fileUpload.completed) {
          if (fileUpload.emitMetrics) {
            // TODO: Use more meaningful event type
            trackEvent('click', 's3-upload-completed', metrics);
            fileUpload.emitMetrics = false;
          }
          onSingleUploadComplete && onSingleUploadComplete(fileUpload);
        } else {
          allCompleted = false;
        }

        if (fileUpload.errorOccurred) {
          hasError = true;
          if (fileUpload.emitMetrics) {
            // TODO: Use more meaningful event type
            trackError('click', new Error(fileUpload.errorMessage), metrics);
            fileUpload.emitMetrics = false;
          }
        }
      }
      if (hasError) {
        setLoading(false);
        onUploadError && onUploadError();
      }

      if (allCompleted) {
        setLoading(false);
        onAllUploadComplete && onAllUploadComplete(updatedFiles);
      } else {
        setLoading(true);
      }
      return updatedFiles;
    });
  };

  /**
   * onFileChange is called when the value in the file input changes (when user select the file).
   * for each file in the list:
   * 1. it calls uploadFile to upload the file.
   * 2. during the upload, it gets update of uploading progress by passing onFileUploadUpdate callback.
   * 3. after upload fails or completes, it gets update of upload result by passing onFileUploadUpdate callback.
   * More details about when onFileUploadUpdate callback is used, can be found in uploadFileToS3 helper function.
   */
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.files) {
      setLoading(true);
      onUploadStart && onUploadStart();
      const newUploads: FileUpload[] = [];
      Array.from(e.currentTarget.files).forEach((file) => {
        const fileUpload: FileUpload = {
          file: file,
          s3Key: getS3Key(file),
          progress: 0,
          uploadLatency: 0,
          completed: false,
          errorOccurred: false,
          emitMetrics: false,
          errorMessage: '',
        };
        uploadFile(fileUpload, onFileUploadUpdate);
        newUploads.push(fileUpload);
      });
      setFiles(newUploads);
    }
  };

  function renderUploadButton() {
    return (
      <Flex flexDirection={['column', null, 'row']} alignItems={['start', null, 'center']}>
        <UploadButton
          analyticsName={`cake-file-upload-card-${buttonText.toLowerCase().split(' ').join('-')}`}
          aria-label={`${modalDisplayTitle} upload`}
          accept={accept}
          multiple={multiple}
          variant={variant ?? 'secondary'}
          size={size ?? 'sm'}
          onChange={onFileChange}
          disabled={uploadDisabled}
          width={width ?? 'fit-content'}
        >
          {buttonText}
        </UploadButton>
        {optionalField}
      </Flex>
    );
  }

  if (files.length === 0) {
    if (addAnother) {
      return (
        <UploadButton
          analyticsName={`cake-file-upload-card-${buttonText.toLowerCase().split(' ').join('-')}`}
          aria-label={null}
          accept={accept}
          variant="link"
          width="auto"
          height="auto"
          onChange={onFileChange}
          disabled={uploadDisabled}
          p={0}
        >
          {buttonText}
        </UploadButton>
      );
    }
    if (isMobile || renderCtaWithoutCardContainer) {
      return renderUploadButton();
    }

    return (
      <Card variant={cardVariant ?? 'secondary'} padding={7}>
        <Box>{renderUploadButton()}</Box>
      </Card>
    );
  }

  return (
    <>
      {files.map((file: FileUpload, index: number) => (
        <Box width="100%" key={`file-upload-card-${file.s3Key}`}>
          <UploadCard
            data-testid={`upload-card-${file.file.name}`}
            thumbnailUrl={getFileThumbnailUrl(file)}
            key={file.s3Key}
            variant={cardVariant}
            progress={file.progress}
            fileAdded={!loading && file.completed}
            errorMessage={file.errorOccurred ? 'Please try again!' : undefined}
            title={file.file.name}
            mb={index === files.length - 1 ? 0 : 8}
            onCancel={deleteFile && file.completed ? () => setShowDeleteModal(true) : undefined}
            onClick={
              file.downloadUrl && file.completed ? () => setShowDisplayModal(true) : undefined
            }
            cancelLabel="Delete this file"
          />
          {deleteFile && (
            <Modal
              analyticsName="cake-file-upload-card-delete-file-modal"
              mobileSize="sheet"
              isOpen={showDeleteModal}
              headerProps={{ title: 'Delete file', titleProps: { as: 'h2' } }}
              onRequestClose={() => setShowDeleteModal(false)}
            >
              <Box>
                <Text mb={8}>Are you sure you want to delete this file?</Text>
                <Button
                  aria-label="Cancel deleting photo"
                  mb={3}
                  variant="secondary"
                  analyticsName="cancel-delete-photo-cta"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  aria-label="Delete photo"
                  analyticsName="delete-photo-cta"
                  onClick={() => {
                    deleteFile(file);
                    setShowDeleteModal(false);
                  }}
                >
                  Delete
                </Button>
              </Box>
            </Modal>
          )}
          {file.downloadUrl && (
            <Modal
              analyticsName="cake-file-upload-card-file-display-modal"
              isOpen={showDisplayModal}
              headerProps={{ title: modalDisplayTitle, titleProps: { as: 'h2' } }}
              onRequestClose={() => setShowDisplayModal(false)}
              desktopAndTabletSize="fullScreen"
            >
              <Flex height="100%" justifyContent="center" alignItems="center" width="100%">
                {renderFileDisplay(file)}
              </Flex>
            </Modal>
          )}
        </Box>
      ))}
    </>
  );
};

function getFileThumbnailUrl(fileUpload: FileUpload) {
  const fileType = getFileType(fileUpload.file);
  if (fileType?.startsWith('image')) {
    return fileUpload.downloadUrl;
  }
  if (fileType?.startsWith('video')) {
    // video thumbnail placeholder
    return 'https://imgdrop.imgix.net/e9e40eeccdfd4420b2fa43bbca5462c0.png';
  }
  // pdf thumbnail placeholder
  return 'https://imgdrop.imgix.net/b8b2714b0ad847368345a84392ed8ddb.png';
}

function renderFileDisplay(fileUpload: FileUpload) {
  const fileType = getFileType(fileUpload.file);
  if (fileType?.startsWith('image')) {
    return (
      <Image src={fileUpload.downloadUrl} alt="The image that you just uploaded" maxWidth="100%" />
    );
  }
  if (fileType?.startsWith('video')) {
    return (
      <ReactPlayer
        url={fileUpload.downloadUrl}
        playing={true}
        controls={true}
        width="100%"
        height="100%"
      />
    );
  }
  return (
    <iframe width="100%" height="100%" title={fileUpload.file.name} src={fileUpload.downloadUrl} />
  );
}
